import { useCallback, useEffect, useMemo, useState } from "react";
import baseUrl from "../../api/baseUrl";
import LabPartnerDetailModal from "../../components/labs/LabPartnerDetailModal";
import LabPartnerRemoveDialog from "../../components/labs/LabPartnerRemoveDialog";
import LocalLabPriceListModal from "../../components/labs/LocalLabPriceListModal";
import {
  emptyLocalLabForm,
  formatRemovedDate,
  REMOVAL_REASON_LABELS,
  type DetailModalMode,
  type DirectoryLab,
  type LocalLabForm,
  type Partner,
  type RemovalReason,
} from "../../components/labs/labPartnerTypes";

function LabPartners() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [removedPartners, setRemovedPartners] = useState<Partner[]>([]);
  const [directory, setDirectory] = useState<DirectoryLab[]>([]);
  const [directoryLoading, setDirectoryLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [removedLoading, setRemovedLoading] = useState(true);
  const [addingLabId, setAddingLabId] = useState<string | null>(null);
  const [addingLocalLab, setAddingLocalLab] = useState(false);
  const [showLocalForm, setShowLocalForm] = useState(false);
  const [localForm, setLocalForm] = useState<LocalLabForm>(emptyLocalLabForm());
  const [message, setMessage] = useState("");
  const [directoryQuery, setDirectoryQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [removedOpen, setRemovedOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<DetailModalMode>("view-partner");
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [selectedDirectoryLab, setSelectedDirectoryLab] =
    useState<DirectoryLab | null>(null);
  const [modalSaving, setModalSaving] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Partner | null>(null);
  const [removing, setRemoving] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [priceListPartner, setPriceListPartner] = useState<Partner | null>(
    null
  );

  const load = useCallback(() => {
    setLoading(true);
    baseUrl
      .get("client/labs/partners")
      .then((res) => setPartners(res.data.data ?? []))
      .catch(() => setPartners([]))
      .finally(() => setLoading(false));
  }, []);

  const loadRemoved = useCallback(() => {
    setRemovedLoading(true);
    baseUrl
      .get("client/labs/partners", { params: { status: "removed" } })
      .then((res) => setRemovedPartners(res.data.data ?? []))
      .catch(() => setRemovedPartners([]))
      .finally(() => setRemovedLoading(false));
  }, []);

  const loadDirectory = useCallback((q = "") => {
    setDirectoryLoading(true);
    baseUrl
      .get("client/labs/partners/search", {
        params: q.trim() ? { q: q.trim() } : undefined,
      })
      .then((res) => {
        const list = (res.data.directory ?? []).map(
          (l: Record<string, string>) => ({
            id: l.id,
            labId: l.id,
            name: l.name,
            officialName: l.officialName,
            email: l.email,
            phone: l.contactNumber,
            address: l.address,
          })
        );
        setDirectory(list);
      })
      .catch(() => setDirectory([]))
      .finally(() => setDirectoryLoading(false));
  }, []);

  useEffect(() => {
    load();
    loadRemoved();
  }, [load, loadRemoved]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(directoryQuery), 300);
    return () => window.clearTimeout(t);
  }, [directoryQuery]);

  useEffect(() => {
    loadDirectory(debouncedQuery);
  }, [debouncedQuery, loadDirectory]);

  const isErrorMessage = useMemo(
    () =>
      message.toLowerCase().includes("fail") ||
      message.toLowerCase().includes("required"),
    [message]
  );

  function refreshAll() {
    load();
    loadRemoved();
    loadDirectory(debouncedQuery);
  }

  function addLab(labId: string, isOwnLab = false) {
    if (!labId) return;
    setMessage("");
    setAddingLabId(labId);
    setModalSaving(true);
    baseUrl
      .post("client/labs/partners", { labId, isOwnLab })
      .then((res) => {
        setMessage(res.data?.message || "Lab added to your partners");
        setModalOpen(false);
        setSelectedDirectoryLab(null);
        refreshAll();
      })
      .catch((err) =>
        setMessage(err?.response?.data?.message || "Failed to add lab")
      )
      .finally(() => {
        setAddingLabId(null);
        setModalSaving(false);
      });
  }

  function addLocalLab(e: React.FormEvent) {
    e.preventDefault();
    const labName = localForm.labName.trim();
    if (!labName) {
      setMessage("Lab name is required");
      return;
    }
    setMessage("");
    setAddingLocalLab(true);
    baseUrl
      .post("client/labs/partners", {
        clinicLocalProfile: {
          labName,
          contactName: localForm.contactName.trim() || undefined,
          phone: localForm.phone.trim() || undefined,
          email: localForm.email.trim() || undefined,
          address: localForm.address.trim() || undefined,
        },
        isOwnLab: localForm.isOwnLab,
      })
      .then((res) => {
        setMessage(res.data?.message || "Personal lab added");
        setLocalForm(emptyLocalLabForm());
        setShowLocalForm(false);
        refreshAll();
      })
      .catch((err) =>
        setMessage(err?.response?.data?.message || "Failed to add personal lab")
      )
      .finally(() => setAddingLocalLab(false));
  }

  function openPartnerView(partner: Partner) {
    setSelectedPartner(partner);
    setSelectedDirectoryLab(null);
    setModalMode("view-partner");
    setModalOpen(true);
  }

  function openDirectoryView(lab: DirectoryLab) {
    setSelectedDirectoryLab(lab);
    setSelectedPartner(null);
    setModalMode("view-directory");
    setModalOpen(true);
  }

  function openRemove(partner: Partner) {
    setRemoveTarget(partner);
    setRemoveOpen(true);
  }

  function confirmRemove(payload: {
    removalReason?: RemovalReason;
    removalNote?: string;
  }) {
    if (!removeTarget) return;
    setRemoving(true);
    setMessage("");
    baseUrl
      .delete(`client/labs/partners/${removeTarget.id}`, { data: payload })
      .then((res) => {
        setMessage(res.data?.message || "Partner lab removed");
        setRemoveOpen(false);
        setRemoveTarget(null);
        setModalOpen(false);
        setSelectedPartner(null);
        refreshAll();
      })
      .catch((err) =>
        setMessage(err?.response?.data?.message || "Failed to remove partner")
      )
      .finally(() => setRemoving(false));
  }

  function restorePartner(partner: Partner) {
    setRestoringId(partner.id);
    setModalSaving(true);
    setMessage("");
    baseUrl
      .patch(`client/labs/partners/${partner.id}`, { status: "active" })
      .then((res) => {
        setMessage(res.data?.message || "Partner lab restored");
        setModalOpen(false);
        setSelectedPartner(null);
        refreshAll();
      })
      .catch((err) =>
        setMessage(err?.response?.data?.message || "Failed to restore partner")
      )
      .finally(() => {
        setRestoringId(null);
        setModalSaving(false);
      });
  }

  function toggleOwnLab(isOwnLab: boolean) {
    if (!selectedPartner) return;
    setModalSaving(true);
    setMessage("");
    baseUrl
      .patch(`client/labs/partners/${selectedPartner.id}`, { isOwnLab })
      .then((res) => {
        const updated = res.data?.data as Partner | undefined;
        if (updated) setSelectedPartner(updated);
        setMessage(isOwnLab ? "Marked as your own lab" : "No longer marked as your own lab");
        load();
      })
      .catch((err) => setMessage(err?.response?.data?.message || "Failed to update lab"))
      .finally(() => setModalSaving(false));
  }

  function savePersonalEdit(form: LocalLabForm) {
    if (!selectedPartner) return;
    const labName = form.labName.trim();
    if (!labName) {
      setMessage("Lab name is required");
      return;
    }
    setModalSaving(true);
    setMessage("");
    baseUrl
      .patch(`client/labs/partners/${selectedPartner.id}`, {
        clinicLocalProfile: {
          labName,
          contactName: form.contactName.trim() || undefined,
          phone: form.phone.trim() || undefined,
          email: form.email.trim() || undefined,
          address: form.address.trim() || undefined,
        },
        displayName: labName,
        isOwnLab: form.isOwnLab,
      })
      .then((res) => {
        const updated = res.data?.data as Partner | undefined;
        setMessage("Personal lab updated");
        setModalMode("view-partner");
        if (updated) setSelectedPartner(updated);
        load();
      })
      .catch((err) =>
        setMessage(err?.response?.data?.message || "Failed to update lab")
      )
      .finally(() => setModalSaving(false));
  }

  const closePriceList = useCallback(() => setPriceListPartner(null), []);

  const actionBtn =
    "inline-flex rounded-lg border border-slate-200 dark:border-slate-600 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60";
  const dangerBtn =
    "inline-flex rounded-lg border border-red-200 dark:border-red-900/50 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/30";
  const primaryBtn =
    "inline-flex rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50";

  return (
    <div className="w-full p-4 md:p-6 lg:p-8">
      <h1 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
        Partner labs
      </h1>
      <p className="text-sm text-slate-500 mb-6">
        Manage your lab relationships. Add registered labs from the directory,
        or add your own lab if it is not on the platform yet. Personal labs are
        only visible to your clinic.
      </p>

      {message && (
        <p
          className={`mb-4 text-sm px-3 py-2 rounded-lg ${
            isErrorMessage
              ? "text-red-700 bg-red-50 dark:bg-red-950/40 dark:text-red-300"
              : "text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300"
          }`}
        >
          {message}
        </p>
      )}

      {/* Add personal lab */}
      <div className="mb-6 rounded-xl border dark:border-slate-700 overflow-hidden">
        <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800 border-b dark:border-slate-700">
          <div>
            <h2 className="text-sm font-semibold">Add your own lab</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              For labs not registered on the platform. Only your clinic can see
              this entry.
            </p>
          </div>
          {!showLocalForm && (
            <button
              type="button"
              onClick={() => {
                setMessage("");
                setShowLocalForm(true);
              }}
              className="shrink-0 px-4 py-2 text-sm font-medium text-white rounded-lg bg-primary"
            >
              Add personal lab
            </button>
          )}
        </div>
        {showLocalForm && (
          <form onSubmit={addLocalLab} className="p-4 space-y-4 text-sm">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Lab name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={localForm.labName}
                onChange={(e) =>
                  setLocalForm((prev) => ({ ...prev, labName: e.target.value }))
                }
                placeholder="Enter lab name"
                className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
                required
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Contact name
                </label>
                <input
                  type="text"
                  value={localForm.contactName}
                  onChange={(e) =>
                    setLocalForm((prev) => ({
                      ...prev,
                      contactName: e.target.value,
                    }))
                  }
                  placeholder="Contact person"
                  className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Phone
                </label>
                <input
                  type="text"
                  value={localForm.phone}
                  onChange={(e) =>
                    setLocalForm((prev) => ({ ...prev, phone: e.target.value }))
                  }
                  placeholder="Phone number"
                  className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Email
              </label>
              <input
                type="email"
                value={localForm.email}
                onChange={(e) =>
                  setLocalForm((prev) => ({ ...prev, email: e.target.value }))
                }
                placeholder="Email address"
                className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Address
              </label>
              <textarea
                value={localForm.address}
                onChange={(e) =>
                  setLocalForm((prev) => ({ ...prev, address: e.target.value }))
                }
                placeholder="Lab address"
                rows={2}
                className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
              />
            </div>
            <label className="flex items-start gap-2.5 text-sm">
              <input
                type="checkbox"
                checked={localForm.isOwnLab}
                onChange={(e) => setLocalForm((prev) => ({ ...prev, isOwnLab: e.target.checked }))}
                className="mt-0.5 size-4 rounded border-slate-300"
              />
              <span>
                <span className="block font-medium text-slate-700 dark:text-slate-200">This is our own lab</span>
                <span className="block text-xs text-slate-500">Offered under &quot;Modelled by&quot; on lab orders.</span>
              </span>
            </label>
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="submit"
                disabled={addingLocalLab}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg bg-primary disabled:opacity-50"
              >
                {addingLocalLab ? "Saving…" : "Save personal lab"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowLocalForm(false);
                  setLocalForm(emptyLocalLabForm());
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg border dark:border-slate-600 text-slate-600 dark:text-slate-300"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Directory — always visible */}
      <div className="mb-6 rounded-xl border dark:border-slate-700 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800 border-b dark:border-slate-700 space-y-3">
          <div>
            <h2 className="text-sm font-semibold">
              Registered labs you can add
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Browse platform labs and add them as partners.
            </p>
          </div>
          <input
            type="search"
            value={directoryQuery}
            onChange={(e) => setDirectoryQuery(e.target.value)}
            placeholder="Search labs by name or email…"
            className="w-full max-w-md rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
          />
        </div>
        {directoryLoading ? (
          <p className="p-6 text-sm text-slate-500">Loading labs…</p>
        ) : directory.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">
            {debouncedQuery.trim()
              ? "No labs match your search"
              : "No registered labs available to add"}
          </p>
        ) : (
          <ul className="divide-y dark:divide-slate-700">
            {directory.map((lab) => (
              <li
                key={lab.id}
                className="px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-800 dark:text-white">
                    {lab.name}
                  </p>
                  {lab.address?.trim() && (
                    <p className="text-slate-500 text-xs mt-0.5">
                      {lab.address.trim()}
                    </p>
                  )}
                  <p className="text-slate-500 text-xs mt-0.5">
                    {[lab.email, lab.phone]
                      .map((v) => v?.trim())
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => openDirectoryView(lab)}
                    className={actionBtn}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    onClick={() => addLab(lab.labId)}
                    disabled={addingLabId === lab.labId}
                    className={primaryBtn}
                  >
                    {addingLabId === lab.labId ? "Adding…" : "Add"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Active partners */}
      <div className="mb-6 rounded-xl border dark:border-slate-700 overflow-x-auto">
        <h2 className="px-4 py-3 text-sm font-semibold bg-slate-50 dark:bg-slate-800 border-b dark:border-slate-700">
          Your partner labs
        </h2>
        {loading ? (
          <p className="p-6 text-sm text-slate-500">Loading…</p>
        ) : partners.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">
            No partner labs yet. Add one from the directory or create a personal
            lab above.
          </p>
        ) : (
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/80">
                <th className="px-4 py-3 font-medium text-slate-500">Lab</th>
                <th className="px-4 py-3 font-medium text-slate-500">Type</th>
                <th className="px-4 py-3 font-medium text-slate-500">Address</th>
                <th className="px-4 py-3 font-medium text-slate-500">Contact</th>
                <th className="px-4 py-3 font-medium text-slate-500 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-slate-100 dark:border-slate-700/80 hover:bg-slate-50/80 dark:hover:bg-slate-800/50"
                >
                  <td className="px-4 py-3 font-medium text-slate-800 dark:text-white">
                    {p.name?.trim() || "Lab"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        p.isShadow
                          ? "inline-flex rounded-full bg-slate-100 dark:bg-slate-700 px-2.5 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-300"
                          : "inline-flex rounded-full bg-emerald-50 dark:bg-emerald-950/50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300"
                      }
                    >
                      {p.isShadow ? "Added locally" : "Registered lab"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                    {p.address?.trim() || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                    {[p.contactName, p.email, p.phone]
                      .map((v) => v?.trim())
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openPartnerView(p)}
                        className={actionBtn}
                      >
                        View
                      </button>
                      {p.isShadow && (
                        <button
                          type="button"
                          onClick={() => setPriceListPartner(p)}
                          className={actionBtn}
                        >
                          Price list
                        </button>
                      )}
                      {p.isShadow && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedPartner(p);
                            setModalMode("edit-personal");
                            setModalOpen(true);
                          }}
                          className={actionBtn}
                        >
                          Edit
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openRemove(p)}
                        className={dangerBtn}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Removed partners */}
      <div className="rounded-xl border dark:border-slate-700 overflow-hidden">
        <button
          type="button"
          onClick={() => setRemovedOpen((v) => !v)}
          className="w-full px-4 py-3 flex items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800 border-b dark:border-slate-700 text-left"
        >
          <h2 className="text-sm font-semibold">
            Removed partners
            {!removedLoading && (
              <span className="ml-2 font-normal text-slate-500">
                ({removedPartners.length})
              </span>
            )}
          </h2>
          <span className="text-xs text-slate-500">
            {removedOpen ? "Hide" : "Show"}
          </span>
        </button>
        {removedOpen &&
          (removedLoading ? (
            <p className="p-6 text-sm text-slate-500">Loading…</p>
          ) : removedPartners.length === 0 ? (
            <p className="p-6 text-sm text-slate-500">
              No removed partners. Removed labs appear here so you can restore
              them without losing order history.
            </p>
          ) : (
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/80">
                  <th className="px-4 py-3 font-medium text-slate-500">Lab</th>
                  <th className="px-4 py-3 font-medium text-slate-500">Type</th>
                  <th className="px-4 py-3 font-medium text-slate-500">
                    Removed
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-500">
                    Reason
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-500 text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {removedPartners.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-slate-100 dark:border-slate-700/80"
                  >
                    <td className="px-4 py-3 font-medium text-slate-800 dark:text-white">
                      {p.name?.trim() || "Lab"}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                      {p.isShadow ? "Added locally" : "Registered lab"}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                      {formatRemovedDate(p.removedAt)}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                      {p.removalReason
                        ? REMOVAL_REASON_LABELS[
                            p.removalReason as RemovalReason
                          ] || p.removalReason
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openPartnerView(p)}
                          className={actionBtn}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => restorePartner(p)}
                          disabled={restoringId === p.id}
                          className={primaryBtn}
                        >
                          {restoringId === p.id ? "Restoring…" : "Restore"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
      </div>

      <LabPartnerDetailModal
        open={modalOpen}
        mode={modalMode}
        partner={selectedPartner}
        directoryLab={selectedDirectoryLab}
        saving={modalSaving}
        onClose={() => {
          if (modalSaving) return;
          setModalOpen(false);
          setSelectedPartner(null);
          setSelectedDirectoryLab(null);
        }}
        onEdit={() => setModalMode("edit-personal")}
        onCancelEdit={() => setModalMode("view-partner")}
        onRemove={() => {
          if (selectedPartner) openRemove(selectedPartner);
        }}
        onRestore={() => {
          if (selectedPartner) restorePartner(selectedPartner);
        }}
        onAdd={(isOwnLab) => {
          if (selectedDirectoryLab) addLab(selectedDirectoryLab.labId, isOwnLab);
        }}
        onSavePersonal={savePersonalEdit}
        onToggleOwnLab={toggleOwnLab}
      />

      <LocalLabPriceListModal
        partner={priceListPartner}
        onClose={closePriceList}
      />

      <LabPartnerRemoveDialog
        open={removeOpen}
        partner={removeTarget}
        submitting={removing}
        onClose={() => {
          if (removing) return;
          setRemoveOpen(false);
          setRemoveTarget(null);
        }}
        onConfirm={confirmRemove}
      />
    </div>
  );
}

export default LabPartners;
